'use client';

import { ClickableDiv } from '@wwe-portal/ui/components/button/clickable-div';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons/icomoon';
import {
  EventData,
  Milestone,
  UniUserEventProgress,
} from '@wwe-portal/entities/rbe.type';
import {
  createPortableDialog,
  Dialog,
  DialogCloseButton,
  DialogContent,
  showDialog,
  usePortableDialog,
} from '@wwe-portal/ui/components/dialog';
import { FC } from 'react';
import { S3_IMAGES_URL } from '@wwe-portal/web/utils/config';
import Image from 'next/image';
import { cn } from '@wwe-portal/ui/lib/lib';
import { useTimeLeft } from '@wwe-portal/ui/lib/hooks';

import styles from './event-banner.module.css';
import { ProgressBar } from '@wwe-portal/ui/components/progress';
import { formatThousands } from '@wwe-portal/utils/format';
import { useIsAuthenticated } from '@wwe-portal/web/providers/stores/auth-store';
import {
  countEventParticipant,
  getUserEventPoints,
  isGlobalEventProgress,
} from '@wwe-portal/web/utils/rbe/helper';

const EventDetailDialog = createPortableDialog(
  ({
    eventData,
    eventType,
    userProgress,
    currentMilestone,
  }: {
    eventData: EventData;
    eventType: string;
    userProgress: UniUserEventProgress | undefined;
    currentMilestone: Milestone | undefined;
  }) => {
    const { getTimeText, ended } = useTimeLeft(eventData.endDate);
    const modal = usePortableDialog();
    const isLogged = useIsAuthenticated();

    const userPoint = getUserEventPoints(userProgress);
    const participants = countEventParticipant(userProgress);

    const renderYourEventPoints = () => {
      if (typeof userPoint == 'undefined') return null;

      if (isGlobalEventProgress(userProgress) && !isLogged) {
        return null;
      }

      return (
        <div
          className={cn(
            'flex items-center gap-2.5 px-4 py-1',
            styles.dialogTimeLeft
          )}
        >
          <div className="h-6 w-6">
            <IcoMoonIcon
              iconName="medal-fill"
              className="text-yellow800 h-full w-full"
            />
          </div>
          <div className="text-xs">
            <div className="text-sm font-semibold">
              {formatThousands(userPoint)}
            </div>
            <div>Your Event Points</div>
          </div>
        </div>
      );
    };

    return (
      <Dialog
        open={modal.visible}
        onOpenChange={(value) => {
          if (!value) {
            modal.remove();
          }
        }}
      >
        <DialogContent
          variant="dialog"
          className="z-dialog max-w-[calc(100dvw-32px)]"
          title={eventData.name}
        >
          <div className="relative flex flex-col gap-5 overflow-hidden rounded-xl px-5">
            <div className="py-10">
              <h3 className="text-blue700 text-xs font-medium uppercase">
                {eventType}
              </h3>
              <h4 className="text-text300 mb-2 text-2xl font-semibold leading-[40px]">
                {eventData.name}
              </h4>
              <p className="line-clamp-4 text-sm text-white">
                {eventData.description}
              </p>
            </div>
            <div
              className={cn(
                'mb-5 flex items-center gap-2.5 px-4 py-1 text-sm',
                styles.dialogTimeLeft
              )}
            >
              <IcoMoonIcon iconName="timer" className="h-6 w-6" />{' '}
              {ended ? 'Event Ended' : getTimeText(' ', 3)}
            </div>
            <div className={cn('absolute inset-0 -z-10', styles.bannerImage)}>
              <Image
                fill
                src={`${S3_IMAGES_URL}/${eventData.image}`}
                alt={eventData.name}
                className="bg-center object-cover"
              />
            </div>
            {eventData.assetsBanner && eventData.assetsBanner.length > 0 && (
              <div className="mt-5 flex h-[90px] w-full items-center justify-center gap-2">
                {eventData.assetsBanner.map((item) => (
                  <Image
                    width={60}
                    height={90}
                    src={item.url}
                    alt={item.name}
                    key={item.id}
                    className="object-contain"
                  />
                ))}
              </div>
            )}
            {(userPoint != null || participants != null) && (
              <div className="flex flex-col gap-2.5">
                {renderYourEventPoints()}

                {participants != null && (
                  <div
                    className={cn(
                      'flex items-center gap-2.5 px-4 py-1',
                      styles.dialogTimeLeft
                    )}
                  >
                    <div className="h-6 w-6">
                      <IcoMoonIcon
                        iconName="user-add-fill"
                        className="text-yellow800 h-full w-full"
                      />
                    </div>
                    <div className="text-xs">
                      <div className="text-sm font-semibold">
                        {formatThousands(participants)}
                      </div>
                      <div>Participants</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {userProgress && currentMilestone && (
              <div className="bg-back200 -mx-5 flex flex-col items-center gap-5 px-4 py-5">
                <div className="relative">
                  <Image
                    src="/assets/images/limited-time-events/event-trophy.webp"
                    alt="Event Trophy"
                    className="bg-center object-cover"
                    width={64}
                    height={64}
                  />
                  <div className={styles.bannerMilestone}>
                    {currentMilestone.level}
                  </div>
                </div>
                <ProgressBar
                  value={Math.min(
                    userProgress.point,
                    currentMilestone.requirement
                  )}
                  isShowMax
                  maxValue={currentMilestone.requirement}
                  className="w-full"
                />
              </div>
            )}
          </div>

          <DialogCloseButton onClose={() => modal.remove()} />
        </DialogContent>
      </Dialog>
    );
  }
);

export const EventMoreInfo: FC<{
  eventType: string;
  eventData: EventData;
  userProgress: UniUserEventProgress | undefined;
  currentMilestone: Milestone | undefined;
  className?: string;
}> = ({ eventData, userProgress, currentMilestone, eventType, className }) => {
  return (
    <ClickableDiv
      className={cn(
        'bg-back200 border-text600 rounded border p-2.5',
        className
      )}
      onClick={() => {
        showDialog(EventDetailDialog, {
          eventData,
          userProgress,
          currentMilestone,
          eventType,
        });
      }}
    >
      <IcoMoonIcon
        iconName="information-fill"
        className="text-text400 h-[18px] w-[18px]"
      />
    </ClickableDiv>
  );
};
